import * as React from "react";
import clsx from "clsx";
import { ChevronDown, Mail, Shield, Trash2, UserPlus } from "lucide-react";
import { Avatar } from "../../../ui/components/Avatar";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { DropdownMenu, MenuItem } from "../../../ui/components/DropdownMenu";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import type {
  ListViewCardBadge,
  ListViewCTAGroup,
} from "../../../ui/components/ListView/components/ListViewCard";
import { Modal } from "../../../ui/components/Modal";
import { Text } from "../../../ui/components/Text";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import * as productAccessApi from "../../lib/api/product-access";
import type {
  ProductAccessListRow,
  ProductAccessTier,
  ProductAccessTierOrCustom,
} from "../../lib/api/product-access";
import { InviteProductAccessModal } from "./InviteProductAccessModal";
import { useI18n } from "../../lib/i18n";
import {
  ProductAccessTierBadge,
  productAccessTierLabel,
} from "./ProductAccessTierBadge";
import { useWebSocket } from "../../hooks/useWebSocket";
import * as s from "./ProductPage.css";

const TIER_LABELS: Record<string, string> = {
  view: "View Access",
  edit: "Edit Access",
  full: "Full Access",
  custom: "Custom",
};

const TIER_SHORT_LABELS: Record<ProductAccessTier, string> = {
  view: "View",
  edit: "Edit",
  full: "Full",
};

function tierTextClass(tier: ProductAccessTier): string {
  switch (tier) {
    case "full":
      return s.tierTextFull;
    case "edit":
      return s.tierTextEdit;
    case "view":
      return s.tierTextView;
  }
}

function requestedAccessCtaText(tier: ProductAccessTier): React.ReactNode {
  return (
    <span>
      <span className={tierTextClass(tier)}>{TIER_SHORT_LABELS[tier]}</span>
      {" Access requested"}
    </span>
  );
}

function tierBadgeColor(tier: string | null | undefined): string | undefined {
  switch (tier) {
    case "full":
      return "#B91C1C";
    case "edit":
      return "#1D4ED8";
    case "view":
      return "#047857";
    case "custom":
      return "#B45309";
    default:
      return undefined;
  }
}

function tierSwatchClass(tier: ProductAccessTier): string {
  switch (tier) {
    case "full":
      return s.tierMenuSwatchFull;
    case "edit":
      return s.tierMenuSwatchEdit;
    case "view":
      return s.tierMenuSwatchView;
  }
}

function TierChip({
  tier,
  editable,
  onChange,
  title,
  showShield,
}: {
  tier: ProductAccessTierOrCustom | null;
  editable: boolean;
  onChange?: (tier: ProductAccessTier) => void;
  title?: string;
  showShield?: boolean;
}) {
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const label = productAccessTierLabel(tier);
  const colorClass =
    tier === "full"
      ? s.tierChipFull
      : tier === "edit"
        ? s.tierChipEdit
        : tier === "view"
          ? s.tierChipView
          : tier === "custom"
            ? s.tierChipCustom
            : "";

  if (!editable || !onChange) {
    return (
      <ProductAccessTierBadge
        tier={tier}
        title={title}
        showShield={showShield}
      />
    );
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Access level: ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className={clsx(s.tierChipButton, colorClass)}
      >
        {showShield ? <Shield size={11} aria-hidden /> : null}
        {label}
        <ChevronDown size={10} aria-hidden />
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={setOpen}
        closeOnSelect
        onChange={(id) => {
          if (id) onChange(id as ProductAccessTier);
        }}
      >
        {productAccessApi.PRODUCT_ACCESS_TIER_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <span
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <span
                className={clsx(s.tierMenuSwatch, tierSwatchClass(opt.value))}
              />
              {opt.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  );
}

function statusBadgeForRow(row: ProductAccessListRow): ListViewCardBadge | null {
  switch (row.kind) {
    case "pending":
      return { id: "status", label: "Pending", variant: "pending" };
    case "invited":
      return { id: "status", label: "Invited", variant: "invited" };
    default:
      return null;
  }
}

function canEditAccessTier(row: ProductAccessListRow): boolean {
  return Boolean(
    row.userId && (row.kind === "active" || row.kind === "inherited"),
  );
}

export function ProductAccessSection({
  productId,
  productName,
  token,
  canManage,
  hideChrome = false,
  onAccessCountChange,
}: {
  productId: string;
  productName: string;
  token: string | null;
  canManage: boolean;
  /** When true, omit Card + title chrome (parent CollapsibleSection provides it). */
  hideChrome?: boolean;
  onAccessCountChange?: (count: number) => void;
}) {
  const { t } = useI18n();
  const [rows, setRows] = React.useState<ProductAccessListRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  const [confirm, setConfirm] = React.useState<{
    title: string;
    message: string;
    action: () => Promise<void>;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const { subscribe, unsubscribe } = useWebSocket();

  React.useEffect(() => {
    onAccessCountChange?.(rows.length);
  }, [rows.length, onAccessCountChange]);

  const loadRows = React.useCallback(async () => {
    if (!token || !canManage) return;
    const data = await productAccessApi.fetchProductAccessList(token, productId);
    setRows(productAccessApi.toProductAccessListRows(data));
    setListRefreshKey((k) => k + 1);
  }, [token, productId, canManage]);

  const reload = React.useCallback(async () => {
    try {
      setError(null);
      await loadRows();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load product access"));
    }
  }, [loadRows]);

  React.useEffect(() => {
    if (!token || !canManage) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void productAccessApi
      .fetchProductAccessList(token, productId)
      .then((data) => {
        if (cancelled) return;
        setRows(productAccessApi.toProductAccessListRows(data));
        setListRefreshKey((k) => k + 1);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(getApiErrorMessage(e, "Failed to load product access"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, productId, canManage]);

  // Live updates when anyone requests/changes access on this product.
  React.useEffect(() => {
    if (!canManage) return;
    const topic = `entity:product-access:product:${productId}`;
    const handler = () => {
      void reload();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [canManage, productId, subscribe, unsubscribe, reload]);

  const fetchList = React.useCallback(
    async (params: ListParams): Promise<ListResult<ProductAccessListRow>> => {
      const q = (params.search ?? "").trim().toLowerCase();
      const filtered = q
        ? rows.filter(
            (r) =>
              r.displayName.toLowerCase().includes(q) ||
              r.email.toLowerCase().includes(q),
          )
        : rows;
      return {
        data: filtered,
        total: filtered.length,
      };
    },
    [rows],
  );

  const listContextKey = React.useMemo(
    () =>
      `${listRefreshKey}:${rows
        .map((r) => `${r.id}:${r.kind}:${r.tier}:${r.requestedTier ?? ""}`)
        .join(",")}`,
    [listRefreshKey, rows],
  );

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      await confirm.action();
      setConfirm(null);
      await reload();
    } catch (e) {
      setError(getApiErrorMessage(e, "Action failed"));
      setConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = React.useMemo<ColumnDefinition<ProductAccessListRow>[]>(
    () => [
      {
        id: "member",
        label: "User",
        render: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {row.kind === "invited" ? (
              <Mail size={20} />
            ) : (
              <Avatar
                email={row.email}
                firstName={row.firstName}
                lastName={row.lastName}
                image={normalizeAvatarUrl(row.avatarUrl) || undefined}
                size={32}
              />
            )}
            <div>
              <div style={{ fontWeight: 600 }}>{row.displayName}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{row.email}</div>
            </div>
          </div>
        ),
      },
      {
        id: "tier",
        label: "Access",
        render: (row) => {
          const editable = Boolean(token && canEditAccessTier(row));
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TierChip
                tier={row.tier}
                editable={editable}
                title={
                  row.kind === "inherited" || row.kind === "active"
                    ? row.sourceLabel
                    : undefined
                }
                showShield={row.kind === "inherited" || row.kind === "active"}
                onChange={(tier) => {
                  if (!token || !row.userId) return;
                  const inherited = row.kind === "inherited";
                  setConfirm({
                    title: "Change access level",
                    message: inherited
                      ? `Give ${row.displayName} ${TIER_LABELS[tier]} to this product? This overrides their inherited organization access for this product only.`
                      : `Change ${row.displayName} to ${TIER_LABELS[tier]}?`,
                    action: async () => {
                      if (inherited) {
                        await productAccessApi.upsertProductAccessGrant(
                          token,
                          productId,
                          { userId: row.userId!, tier },
                        );
                      } else {
                        await productAccessApi.updateProductAccessGrant(
                          token,
                          productId,
                          row.userId!,
                          tier,
                        );
                      }
                    },
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        id: "actions",
        label: "",
        enableHiding: false,
        render: (row) => {
          if (!token) return null;
          if (row.kind === "pending" && row.userId) {
            const requested = (row.requestedTier ?? "view") as ProductAccessTier;
            return (
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  type="button"
                  size="sm"
                  color="primary"
                  onClick={() =>
                    setConfirm({
                      title: "Approve access request",
                      message: `Grant ${TIER_LABELS[requested] ?? "View Access"} to ${row.displayName}?`,
                      action: async () => {
                        await productAccessApi.approveProductAccessRequest(
                          token,
                          productId,
                          row.userId!,
                          requested,
                        );
                      },
                    })
                  }
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setConfirm({
                      title: "Deny access request",
                      message: `Deny ${row.displayName}'s request?`,
                      action: async () => {
                        await productAccessApi.denyProductAccessRequest(
                          token,
                          productId,
                          row.userId!,
                        );
                      },
                    })
                  }
                >
                  Deny
                </Button>
              </div>
            );
          }
          if (row.kind === "invited" && row.inviteId) {
            return (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                iconLeft={<Trash2 size={14} />}
                onClick={() =>
                  setConfirm({
                    title: "Cancel invitation",
                    message: `Cancel invite to ${row.email}?`,
                    action: async () => {
                      await productAccessApi.cancelProductAccessInvite(
                        token,
                        productId,
                        row.inviteId!,
                      );
                    },
                  })
                }
              >
                Cancel
              </Button>
            );
          }
          if (row.kind === "active" && row.userId) {
            return (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                iconLeft={<Trash2 size={14} />}
                onClick={() =>
                  setConfirm({
                    title: "Remove access",
                    message: `Remove ${row.displayName}'s access to this product?`,
                    action: async () => {
                      await productAccessApi.removeProductAccessGrant(
                        token,
                        productId,
                        row.userId!,
                      );
                    },
                  })
                }
              />
            );
          }
          return null;
        },
      },
    ],
    [token, productId],
  );

  if (!canManage) return null;

  const body = (
    <>
        {error ? (
          <div style={{ marginBottom: 12 }}>
            <Callout variant="error">{error}</Callout>
          </div>
        ) : null}

        {loading ? (
          <Text level="body-small">{t("pro.products.loadingAccess")}</Text>
        ) : (
          <ListView<ProductAccessListRow>
            listId={`product-access-${productId}`}
            columns={columns}
            fetchList={fetchList}
            contextKey={listContextKey}
            queryOptions={{ staleTime: Infinity }}
            enabledViews={["stack", "table"]}
            headerActions={
              canManage
                ? [
                    {
                      id: "invite",
                      label: "Invite",
                      icon: <UserPlus size={14} />,
                      color: "primary",
                      onClick: () => setInviteOpen(true),
                    },
                  ]
                : undefined
            }
            getRowCtas={(row) => {
              if (!token || row.kind !== "pending" || !row.userId) return undefined;
              const requested = (row.requestedTier ?? "view") as ProductAccessTier;
              const groups: ListViewCTAGroup[] = [
                {
                  id: "approve-deny",
                  text: requestedAccessCtaText(requested),
                  buttons: [
                    {
                      id: "approve",
                      label: "Approve",
                      variant: "primary",
                      onClick: () =>
                        setConfirm({
                          title: "Approve access request",
                          message: `Grant ${TIER_LABELS[requested] ?? "View Access"} to ${row.displayName}?`,
                          action: async () => {
                            await productAccessApi.approveProductAccessRequest(
                              token,
                              productId,
                              row.userId!,
                              requested,
                            );
                          },
                        }),
                    },
                    {
                      id: "deny",
                      label: "Deny",
                      variant: "secondary",
                      onClick: () =>
                        setConfirm({
                          title: "Deny access request",
                          message: `Deny ${row.displayName}'s request?`,
                          action: async () => {
                            await productAccessApi.denyProductAccessRequest(
                              token,
                              productId,
                              row.userId!,
                            );
                          },
                        }),
                    },
                  ],
                },
              ];
              return groups;
            }}
            getContextActions={(row) => {
              if (!token) return [];
              const actions: ListViewContextAction[] = [];
              if (row.kind === "active" && row.userId) {
                actions.push({
                  id: "remove",
                  label: "Remove access",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  bulk: false,
                  onClick: () =>
                    setConfirm({
                      title: "Remove access",
                      message: `Remove ${row.displayName}'s access to this product?`,
                      action: async () => {
                        await productAccessApi.removeProductAccessGrant(
                          token,
                          productId,
                          row.userId!,
                        );
                      },
                    }),
                });
              }
              if (row.kind === "invited" && row.inviteId) {
                actions.push({
                  id: "cancel-invite",
                  label: "Cancel invite",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  bulk: false,
                  onClick: () =>
                    setConfirm({
                      title: "Cancel invitation",
                      message: `Cancel invite to ${row.email}?`,
                      action: async () => {
                        await productAccessApi.cancelProductAccessInvite(
                          token,
                          productId,
                          row.inviteId!,
                        );
                      },
                    }),
                });
              }
              return actions;
            }}
            renderStackCard={(row, ctx) => {
              const image =
                row.kind === "invited" ? (
                  <Mail size={28} style={{ color: "#9CA3AF" }} />
                ) : (
                  <Avatar
                    email={row.email}
                    firstName={row.firstName}
                    lastName={row.lastName}
                    image={normalizeAvatarUrl(row.avatarUrl) || undefined}
                    size={ctx.compact ? 40 : 72}
                  />
                );

              const canEditTier = Boolean(token && canEditAccessTier(row));
              const showSourceChip =
                row.kind === "inherited" || row.kind === "active";
              const tierLabel = row.tier
                ? (TIER_LABELS[row.tier] ?? "—")
                : "No access";
              const tierBadge: ListViewCardBadge = {
                id: "tier",
                label: tierLabel,
                color: tierBadgeColor(row.tier),
                title: showSourceChip ? row.sourceLabel : undefined,
                icon: showSourceChip ? (
                  <Shield size={11} aria-hidden />
                ) : undefined,
                actions: canEditTier
                  ? productAccessApi.PRODUCT_ACCESS_TIER_OPTIONS.map((opt) => ({
                      id: opt.value,
                      label: opt.label,
                      color: tierBadgeColor(opt.value),
                      disabled: opt.value === row.tier,
                      onClick: () => {
                        const inherited = row.kind === "inherited";
                        setConfirm({
                          title: "Change access level",
                          message: inherited
                            ? `Give ${row.displayName} ${TIER_LABELS[opt.value]} to this product? This overrides their inherited organization access for this product only.`
                            : `Change ${row.displayName} to ${TIER_LABELS[opt.value]}?`,
                          action: async () => {
                            if (inherited) {
                              await productAccessApi.upsertProductAccessGrant(
                                token!,
                                productId,
                                { userId: row.userId!, tier: opt.value },
                              );
                            } else {
                              await productAccessApi.updateProductAccessGrant(
                                token!,
                                productId,
                                row.userId!,
                                opt.value,
                              );
                            }
                          },
                        });
                      },
                    }))
                  : undefined,
              };

              const statusBadge = statusBadgeForRow(row);
              const badges: ListViewCardBadge[] = [
                ...(statusBadge ? [statusBadge] : []),
                tierBadge,
              ];

              const variant =
                row.kind === "pending"
                  ? ("attention" as const)
                  : row.kind === "invited"
                    ? ("static" as const)
                    : ("default" as const);

              return (
                <ListViewCard
                  id={row.id}
                  layout="stack"
                  imageFormat="avatar"
                  image={image}
                  title={row.displayName}
                  subtitle={row.kind === "invited" ? undefined : row.email}
                  badges={badges}
                  footerText={row.sourceLabel}
                  variant={variant}
                  compact={ctx.compact}
                  animatedHighlight={row.kind === "pending"}
                />
              );
            }}
            compact
            disableLocalPersistence
            defaultPageSize={25}
          />
        )}

        <InviteProductAccessModal
          open={inviteOpen}
          setOpen={setInviteOpen}
          productId={productId}
          productName={productName}
          token={token}
          onInvited={reload}
        />

        <Modal
          open={Boolean(confirm)}
          setOpen={(open) => {
            if (!open) setConfirm(null);
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 8 }}>
            <Text level="h3" weight="bold">
              {confirm?.title}
            </Text>
            <Text level="body">{confirm?.message}</Text>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 8,
              }}
            >
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirm(null)}
                disabled={confirmLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={() => void runConfirm()}
                disabled={confirmLoading}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
    </>
  );

  if (hideChrome) {
    return body;
  }

  return (
    <Card maxWidth={960} className={s.sectionCard}>
      <div className={s.cardInner}>{body}</div>
    </Card>
  );
}
